import { Editor } from 'grapesjs';
import { FIESTA_IFRAME_MESSAGE_ACTIONS } from '../fiesta.constants';
import { useState, useEffect, useCallback, useRef } from 'react';

const useUndoRedoAvailability = (editor: Editor) => {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const rafIdRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);
  const hasUserActionRef = useRef(false);

  useEffect(() => {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: FIESTA_IFRAME_MESSAGE_ACTIONS.UNDO_REDO_AVAILABILITY, data: { undo: canUndo, redo: canRedo } },
          '*'
        );
      }
    } catch (error) {
      console.warn('Failed to send undo/redo availability message:', error);
    }
  }, [canUndo, canRedo]);

  const updateUndoRedoState = useCallback(() => {
    if (!editor || !editor.UndoManager) {
      return;
    }

    // Only allow undo/redo if we've had user actions and the editor is properly initialized
    const canUndoState = hasUserActionRef.current && editor.UndoManager.hasUndo();
    const canRedoState = hasUserActionRef.current && editor.UndoManager.hasRedo();

    setCanUndo(canUndoState);
    setCanRedo(canRedoState);
  }, [editor]);

  // Debounced update using requestAnimationFrame for better performance
  const debouncedUpdate = useCallback(() => {
    if (rafIdRef.current) {
      window.cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = window.requestAnimationFrame(updateUndoRedoState);
  }, [updateUndoRedoState]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    // Reset state when editor changes
    isInitializedRef.current = false;
    hasUserActionRef.current = false;
    setCanUndo(false);
    setCanRedo(false);

    // Track user actions that should enable undo/redo
    const trackUserAction = () => {
      hasUserActionRef.current = true;
      debouncedUpdate();
    };

    // Listen for undo/redo operations
    editor.on('undo', debouncedUpdate);
    editor.on('redo', debouncedUpdate);

    // Listen for component changes (these are user actions)
    editor.on('component:add', trackUserAction);
    editor.on('component:remove', trackUserAction);
    editor.on('component:update', trackUserAction);

    // Listen for style changes (these are user actions)
    editor.on('style:property:update', trackUserAction);
    editor.on('style:update', trackUserAction);

    // Listen for attribute changes (these are user actions)
    editor.on('component:selected:add', trackUserAction);
    editor.on('component:selected:remove', trackUserAction);

    // Listen for canvas changes (these are user actions)
    editor.on('canvas:drop', trackUserAction);

    // Listen for undo manager specific events (but don't update state during initialization)
    const onUndoAdd = () => {
      if (hasUserActionRef.current) {
        debouncedUpdate();
      }
    };
    const onUndoClear = () => {
      if (hasUserActionRef.current) {
        debouncedUpdate();
      }
    };

    editor.on('undo:add', onUndoAdd);
    editor.on('undo:clear', onUndoClear);

    // Initialize state after the editor is fully ready
    const initializeState = () => {
      isInitializedRef.current = true;
      // Don't call updateUndoRedoState here - let it be false until user actions occur
    };

    if (editor.UndoManager) {
      initializeState();
    } else {
      // Wait for the editor to be ready before initializing
      editor.once('ready', initializeState);
    }

    // Cleanup function to remove event listeners
    return () => {
      if (rafIdRef.current) {
        window.cancelAnimationFrame(rafIdRef.current);
      }
      editor.off('undo', debouncedUpdate);
      editor.off('redo', debouncedUpdate);
      editor.off('component:add', trackUserAction);
      editor.off('component:remove', trackUserAction);
      editor.off('component:update', trackUserAction);
      editor.off('style:property:update', trackUserAction);
      editor.off('style:update', trackUserAction);
      editor.off('component:selected:add', trackUserAction);
      editor.off('component:selected:remove', trackUserAction);
      editor.off('canvas:drop', trackUserAction);
      editor.off('undo:add', onUndoAdd);
      editor.off('undo:clear', onUndoClear);
    };
  }, [editor, updateUndoRedoState, debouncedUpdate]);

  return { canUndo, canRedo };
};

export default useUndoRedoAvailability;
